import { useCallback, useEffect, useState } from "react";
import { fetchAdminBeneficiaries, type AdminBeneficiaryRow } from "../api/adminApi";
import type { Beneficiary } from "../types";
import { getAuthToken } from "../auth";

function mapApiBeneficiary(row: AdminBeneficiaryRow): Beneficiary {
  const birthLabel = row.birth_date
    ? row.age
      ? `${row.birth_date} (${row.age})`
      : row.birth_date
    : "";

  return {
    id: row.id,
    recordId: row.record_id,
    beneficiaryType: row.type,
    photo: row.photo,
    name: row.name,
    relation: row.relation,
    birthDate: birthLabel,
    age: row.age,
    gender: row.gender === "M" ? "M" : row.gender === "F" ? "F" : row.gender,
    employee: row.employee,
    matricule: row.matricule,
    companyMatricule: row.company_matricule,
    company: row.company,
    status: row.status,
  };
}

export function useAdminBeneficiaries() {
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setBeneficiaries([]);
      setTotal(0);
      setError("Connexion requise pour charger les bénéficiaires.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response = await fetchAdminBeneficiaries();
      const mapped = response.beneficiaries.map(mapApiBeneficiary);
      setBeneficiaries(mapped);
      setTotal(response.total);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Impossible de charger les bénéficiaires.";
      setError(message);
      setBeneficiaries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { beneficiaries, total, loading, error, usingApi, reload: load };
}
